import { homeKeys } from '@/features/home/queries/query-keys';
import { pushLog } from '@/libs/debug-log/log-store';
import { safeStringify } from '@/libs/debug-log/serialize';
import { queryClient } from '@/libs/tanstack-query/query-client';
import { useSessionStore } from '@/stores';
import * as Sentry from '@sentry/react-native';

/**
 * @deprecated Use captureMessage instead.
 */
export const log = (message?: any, ...optionalParams: any[]) => {
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.log(message, ...optionalParams);
  }
};

/**
 * @deprecated Use captureException instead.
 */
export const errorLog = (message?: any, ...optionalParams: any[]) => {
  if (__DEV__) {
    console.error(message, ...optionalParams);
  }
};

/**
 *
 * @param message
 * @param optionalParams
 * @deprecated Use captureMessage instead.
 */
export const warnLog = (message?: any, ...optionalParams: any[]) => {
  if (__DEV__) {
    console.warn(message, ...optionalParams);
  }
};

type Primitive = string | number | boolean | null | undefined;
type Metadata = Record<string, Primitive | Record<string, unknown> | unknown[]>;

type CaptureContext = {
  tags?: Record<string, Primitive>;
  extras?: Metadata;
  fingerprint?: string[];
  level?: Sentry.SeverityLevel;
};

const pickUserMetadata = () => {
  const { user, isAuthenticated, isInitialized } = useSessionStore.getState();
  return {
    user: user
      ? {
          uid: user.uid ?? null,
          email: user.email ?? null,
          displayName: user.displayName ?? null,
          phoneNumber: user.phoneNumber ?? null,
        }
      : null,
    auth: {
      isAuthenticated,
      isInitialized,
    },
  };
};

const pickCoachMetadataFromCache = (uid?: string | null) => {
  if (!uid) return null;
  const coach = queryClient.getQueryData(homeKeys.coach(uid)) as Record<string, unknown> | null;
  if (!coach) return null;

  return {
    id: (coach.id as string | undefined) ?? uid,
    name: (coach.name as string | undefined) ?? null,
    firstname: (coach.firstname as string | undefined) ?? null,
    academy_id: (coach.academy_id as string | undefined) ?? null,
    active_session_id: (coach.active_session_id as string | null | undefined) ?? null,
  };
};

const getDefaultLoggerContext = () => {
  const userMeta = pickUserMetadata();
  const uid = userMeta.user?.uid;
  const coachMeta = pickCoachMetadataFromCache(uid);
  return {
    userMeta,
    coachMeta,
  };
};

/** Keys consumed only to build `Error.message` or unwrap nested errors — rest go to Sentry context. */
const ERROR_PAYLOAD_RESERVED_KEYS = new Set(['message', 'error', 'original', 'err']);

const omitReservedErrorKeys = (obj: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (!ERROR_PAYLOAD_RESERVED_KEYS.has(k)) out[k] = v;
  }
  return out;
};

const hasOwnKeys = (obj: Record<string, unknown>) => Object.keys(obj).length > 0;

/** `JSON.stringify` replacer so nested `Error` and odd values show up in the dashboard instead of `[Object]`. */
const sentryPayloadReplacer = (_key: string, value: unknown): unknown => {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  return value;
};

/**
 * Sentry UI often collapses nested objects as `[Object]` / `[Error]`.
 * Use string values (pretty JSON or error text) so issue details stay readable.
 */
const serializeErrorPayloadForSentry = (data: Record<string, unknown>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(data)) {
    if (v === null || v === undefined) {
      out[k] = String(v);
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = String(v);
      continue;
    }
    if (v instanceof Error) {
      const stack = v.stack ? `\n${v.stack}` : '';
      out[k] = `${v.name}: ${v.message}${stack}`.trim();
      continue;
    }
    try {
      out[k] = JSON.stringify(v, sentryPayloadReplacer, 2);
    } catch {
      out[k] = String(v);
    }
  }
  return out;
};

type CoercedException = {
  error: Error;
  /** Shallow remainder of a plain-object payload (e.g. `code`, `status`, `details`). */
  objectExtras?: Record<string, unknown>;
};

/**
 * Prefer inner `error`/`original`/`err` when callers pass `{ message, error }` payloads.
 * Any other enumerable fields on that object are returned as `objectExtras` for Sentry (not only `message`).
 */
const coerceExceptionInput = (raw: unknown): CoercedException => {
  if (raw instanceof Error) {
    return { error: raw };
  }

  if (raw !== null && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    // `error_payload` is the codebase's conventional wrapper key (captureException({ error_payload: e })).
    // Treat it as the inner error only when it's a real Error, so its code/message surface in the
    // console + Sentry instead of serializing to `{}`. Non-Error payloads keep the old behaviour below.
    const inner =
      obj.error ??
      obj.original ??
      obj.err ??
      (obj.error_payload instanceof Error ? obj.error_payload : undefined);
    if (inner instanceof Error) {
      const objectExtras = omitReservedErrorKeys(obj);

      return {
        error: inner,
        objectExtras: hasOwnKeys(objectExtras) ? objectExtras : undefined,
      };
    }
    const msg =
      typeof obj.message === 'string'
        ? obj.message
        : inner !== undefined && inner !== null
          ? typeof inner === 'string'
            ? inner
            : String(inner)
          : JSON.stringify(raw);
    const objectExtras = omitReservedErrorKeys(obj);
    return {
      error: new Error(msg),
      objectExtras: hasOwnKeys(objectExtras) ? objectExtras : undefined,
    };
  }
  return { error: new Error(String(raw)) };
};

/**
 * Library errors retain stacks rooted in node_modules. Reattach stack at the app's call site so
 * Sentry surfaces your feature code first; original error is chained via `.cause`.
 */
const errorWithCaptureCallSiteStack = (underlying: Error): Error => {
  const report = new Error(underlying.message);
  report.name = underlying.name;

  Object.defineProperty(report, 'cause', {
    value: underlying,
    configurable: true,
    enumerable: false,
    writable: true,
  });

  if (typeof Error.captureStackTrace === 'function') {
    Error.captureStackTrace(report, captureException);
  }

  return report;
};

export function captureException(error: unknown, context?: CaptureContext) {
  const { error: underlying, objectExtras } = coerceExceptionInput(error);
  const forSentry = errorWithCaptureCallSiteStack(underlying);
  const defaultContext = getDefaultLoggerContext();

  // Mirror every reported exception into the in-app debug buffer so testers can
  // export it from the profile long-press panel. This runs in production too
  // (unlike the `__DEV__` console.error below), which is where Sentry-reported
  // errors would otherwise be invisible to the debug panel.
  try {
    const type = context?.tags?.usk_log_type;
    const stack = underlying.stack ? `\n${underlying.stack}` : '';
    const extras = objectExtras ? `\n${safeStringify(objectExtras)}` : '';
    pushLog({
      kind: 'console',
      level: 'error',
      message: `[captureException${type ? `:${type}` : ''}] ${underlying.name}: ${underlying.message}${stack}${extras}`,
    });
  } catch {
    // logging is best-effort
  }

  Sentry.withScope(scope => {
    // Default to error so release issues surface in Issues (debug is often filtered or easy to hide in UI).
    scope.setLevel(context?.level ?? 'error');

    if (objectExtras) {
      scope.setContext('error_payload', serializeErrorPayloadForSentry(objectExtras));
    }

    const user = defaultContext.userMeta.user;
    if (user?.uid) {
      scope.setUser({
        id: user.uid,
        email: user.email ?? undefined,
        username: user.displayName ?? undefined,
      });
    }

    scope.setContext('session', {
      ...defaultContext.userMeta.auth,
      hasUser: Boolean(user),
    });
    scope.setContext('user_meta', defaultContext.userMeta.user ?? {});
    scope.setContext('coach_cache', defaultContext.coachMeta ?? {});
    scope.setTag('usk_log_type', 'exception');

    if (context?.tags) {
      Object.entries(context.tags).forEach(([key, value]) => {
        if (value != null) scope.setTag(key, String(value));
      });
    }

    if (context?.extras) {
      scope.setExtras(context.extras);
    }

    if (context?.fingerprint?.length) {
      scope.setFingerprint(context.fingerprint);
    }

    Sentry.captureException(forSentry);
  });

  if (__DEV__) {
    console.error('[captureException]', underlying, '(reported as call-site)', forSentry, context);
  }
}
